import { useCallback } from "react";
import type { EntityReferenceId } from "../../../app/types.js";
import { ApiError, assertNotOfflineQueued, deleteJson, patchJson, postJson } from "../../../lib/api.js";
import { stableRequestKey } from "../../../lib/requestKey.js";
import { useHousehold } from "../../context.js";
import type { ListItemPayload, SheetListItem } from "./ListSheet.js";

// Direct shopping-list item sync used by the local-first ListSheet. Unlike the
// context `run()` path these calls do NOT flip global busy, show toasts, or kick
// a full "refresh: shopping" — the sheet owns its optimistic state, so the only
// job here is to talk to the API and (quietly) keep other views fresh.

export type CreatedItem = { id: EntityReferenceId };

/**
 * The bulk endpoint caps each request at 100 items, so a large paste or a
 * backlog has to go up in batches.
 *
 * The cap is applied by the CALLER, one batch per flush pass, rather than by
 * looping inside `createBulk` (codex review on #1122). A loop here returns
 * nothing at all when a later batch fails: the ids the earlier batches were
 * already given are thrown away with the rejection, so rows the server HAS
 * created stay dirty with no id and the retry sends every one of them again —
 * the duplicate this issue is about, at a hundred rows a time. Sent a batch per
 * pass, each batch's ids are applied to the sheet before the next is attempted.
 */
export const LIST_SHEET_CREATE_BATCH = 100;

export type ListSheetSync = {
  createBulk: (payloads: ListItemPayload[], rowKeys: string[]) => Promise<CreatedItem[]>;
  update: (id: EntityReferenceId, fields: ListItemPayload) => Promise<void>;
  remove: (id: EntityReferenceId) => Promise<void>;
  restore: (id: EntityReferenceId) => Promise<SheetListItem | null>;
  refreshContext: () => void;
};

/**
 * These writes are owned by THE SHEET, not by the generic offline outbox (#1122).
 *
 * Without `mustBeOnline`, `api()` catches the network `TypeError` first — these
 * paths are queue-safe — serializes the write into the IndexedDB outbox and
 * *resolves* with an `__offlineQueued` marker instead of throwing. `createBulk`
 * then read the marker's absent `added` as "the server created nothing,
 * successfully", the sheet retired the row as saved with `serverId` still null,
 * and the offline banner came DOWN. The create was in the queue all along, so
 * any later edit to that row — filtered as a create, because it still has no id
 * — queued a second one, and the reconnect landed two rows on the server while
 * the sheet held one it could never address.
 *
 * A write this sheet will retry itself must not also sit in a queue that replays
 * it: that is two retry mechanisms for one write, and the duplicate is what they
 * produce between them. The sheet is the better of the two here — it keeps the
 * row dirty, replays the row's CURRENT values rather than the ones the queue
 * froze, and knows which server id the answer belongs to. What it gives up is
 * durability across a reload while still offline; the sheet already warns on
 * unload for exactly that, and a lost draft beats a duplicated list.
 *
 * The endpoints stay queue-safe for everyone else: MealsView and ShoppingView
 * write to them too, fire-and-forget, and are right to be queued.
 */
const OWNED_BY_THE_SHEET = { mustBeOnline: true } as const;

// A failed request that we should keep retrying (offline, transient) versus one
// the server will always reject (validation). Mirrors the shopping outbox rules.
export function isRetryableSyncError(error: unknown): boolean {
  if (error instanceof TypeError) return true; // fetch network failure
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  // Status 0 is api()'s normalized network-layer failure (#278) — which is what
  // a `mustBeOnline` write now throws when the connection drops. The browser
  // does not always know it is offline (captive portal, dropped tunnel), so
  // without this the sheet reads a dropped connection as a permanent rejection
  // and marks every dirty row clean: the user's edits, discarded silently.
  if (error instanceof ApiError) return error.status === 0 || error.status === 401 || error.status === 408 || error.status === 429 || error.status >= 500;
  return false;
}

export function useListSheetSync(listId: EntityReferenceId | null): ListSheetSync {
  const { loadShopping, suppressRealtimeBanner } = useHousehold();

  const createBulk = useCallback(async (payloads: ListItemPayload[], rowKeys: string[]): Promise<CreatedItem[]> => {
    if (!listId || !payloads.length) return [];
    suppressRealtimeBanner();
    // One request, capped by the caller at LIST_SHEET_CREATE_BATCH. Results stay
    // in submission order.
    //
    // Guarded as well as `mustBeOnline`, because THIS is the response the sheet
    // turns into row identity: a marker read as an empty `added` is
    // indistinguishable from "the server created nothing".
    const body = { items: payloads };
    const result = assertNotOfflineQueued(
      await postJson<{ added: CreatedItem[] }>(`/api/v1/shopping-lists/${listId}/items/bulk`, body, {
        ...OWNED_BY_THE_SHEET,
        // The retry of a lost attempt has to reach the server as the SAME
        // request, or its ledger cannot recognise it and the batch lands twice
        // (#1235). `api()` mints a fresh id per call, which is right for the
        // generic outbox — it replays frozen bytes — and wrong here, where the
        // sheet retries by calling again with the row's current values.
        //
        // Derived from the rows and the body together: same rows, same values →
        // same key → the server answers with the batch it already created, in
        // the same order the sheet maps back by position. Edited during the
        // outage → a new key, deliberately, because the recorded response
        // describes a different batch. See `stableRequestKey` for the rule and
        // the case it leaves open.
        idempotencyKey: stableRequestKey(`POST /api/v1/shopping-lists/${listId}/items/bulk#${rowKeys.join(",")}`, body)
      })
    );
    return result.added ?? [];
  }, [listId, suppressRealtimeBanner]);

  const update = useCallback(async (id: EntityReferenceId, fields: ListItemPayload): Promise<void> => {
    suppressRealtimeBanner();
    await patchJson(`/api/v1/shopping-items/${id}`, fields, OWNED_BY_THE_SHEET);
  }, [suppressRealtimeBanner]);

  const remove = useCallback(async (id: EntityReferenceId): Promise<void> => {
    suppressRealtimeBanner();
    await deleteJson<{ ok: true }>(`/api/v1/shopping-items/${id}`, OWNED_BY_THE_SHEET);
  }, [suppressRealtimeBanner]);

  /**
   * Take a removal back (#296).
   *
   * `mustBeOnline` like its siblings, and for a sharper reason: an undo is a
   * button somebody is watching. Queued into the offline outbox it would resolve
   * as a success, put the row back on screen, and replay minutes later against a
   * server whose undo window has long closed — so the sheet would be showing a
   * row that no longer exists anywhere. Better to say the undo did not happen.
   *
   * The server restores the item under its ORIGINAL id, so a double tap or a
   * retry after a lost response answers with the row already back rather than a
   * duplicate. That is why nothing here dedupes: it does not have to.
   */
  const restore = useCallback(async (id: EntityReferenceId): Promise<SheetListItem | null> => {
    suppressRealtimeBanner();
    // The restored ITEM comes back, not just an acknowledgement, because one
    // caller needs its contents rather than only the fact that it worked: a row
    // whose name was CLEARED is still on screen as a blank, and the name it lost
    // is the one thing the sheet no longer has a copy of. The server's snapshot
    // is the only place it survives (#296).
    const result = await postJson<{ restored: boolean; item: SheetListItem }>(`/api/v1/shopping-items/${id}/restore`, {}, OWNED_BY_THE_SHEET);
    return result?.item ?? null;
  }, [suppressRealtimeBanner]);

  // Keep sibling views (home cards, metrics) eventually consistent without
  // yanking the sheet around: a quiet reload, no banner, no busy state.
  const refreshContext = useCallback(() => {
    suppressRealtimeBanner();
    void loadShopping().catch(() => undefined);
  }, [loadShopping, suppressRealtimeBanner]);

  return { createBulk, update, remove, restore, refreshContext };
}
